import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Modal, Box, IconButton } from "@mui/material";
import { FaTimes, FaCamera, FaCheck } from "react-icons/fa";
import { MdCloudUpload } from "react-icons/md";
import BtnLoader from "Components/Common/Loader/BtnLoader";
import uploadSignedImage from "utils/uploadImg";
import { loadDecrypted } from "Common Usage/localStorageUtils";

const style = {
  position: "absolute",
  maxHeight: "90vh",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 640,
  bgcolor: "background.paper",
  border: "none",
  borderRadius: 8,
  boxShadow: 24,
  p: 4,
  overflow: "auto",
  scrollbarWidth: "none",
};

const ChangeAvatarModal = ({
  open,
  onClose,
  userDetails,
  onAvatarUpdated,
}) => {
  const [activeMode, setActiveMode] = useState("upload"); // "upload" | "existing"
  const [avatars, setAvatars] = useState([]);
  const [selectedAvatarId, setSelectedAvatarId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loadingAvatars, setLoadingAvatars] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setActiveMode("upload");
      setSelectedAvatarId(null);
      setPreviewUrl(null);
      setSelectedFile(null);
      setUploading(false);
      setSaving(false);
      setError("");
      setSuccess("");
      fetchAvatars(); // Pre-fetch avatars when modal opens
    }
  }, [open]);

  const fetchAvatars = async () => {
    setLoadingAvatars(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/avatar/admin/get-all`
      );
      setAvatars(res.data?.avatars || []);
    } catch (err) {
      // The API returns 404 when no avatars exist — handle gracefully
      if (err?.response?.status === 404) {
        setAvatars([]);
      } else {
        console.error("Failed to fetch avatars:", err);
        setAvatars([]);
      }
    } finally {
      setLoadingAvatars(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image size should be less than 5MB");
      return;
    }

    setError("");
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setSelectedAvatarId(null); // Clear any selected existing avatar
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");
    setSaving(true);

    try {
      let payload = {};

      if (activeMode === "upload") {
        if (!selectedFile) {
          setError("Please select an image to upload");
          setSaving(false);
          return;
        }

        // Upload the image first
        setUploading(true);
        const uploadedUrl = await uploadSignedImage(selectedFile);
        setUploading(false);

        if (!uploadedUrl) {
          setError("Image upload failed. Please try again.");
          setSaving(false);
          return;
        }

        payload = { avatarUrl: uploadedUrl };
      } else {
        if (!selectedAvatarId) {
          setError("Please select an avatar");
          setSaving(false);
          return;
        }
        payload = { avatarId: selectedAvatarId };
      }

      // Call the admin update avatar API
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/users/admin-update-avatar/${userDetails?._id}`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      setSuccess("Profile picture updated successfully!");

      // Log staff activity
      try {
        const staffData = loadDecrypted("HeartConnect-StaffData");
        const staffId = staffData?._id || staffData?.id || "";
        const staffName = staffData?.fullName || staffData?.name || "Unknown";

        if (staffId) {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/staffs-activity-log/create-staffs-activity`,
            {
              staff: staffId,
              action: "UPDATE_USER_AVATAR",
              description: `Staff ${staffName} updated profile picture for user ${userDetails?.fullName || userDetails?.nickName} (${userDetails?._id})`,
            },
            { headers: { "Content-Type": "application/json" } }
          );
        }
      } catch (logErr) {
        console.error("Failed to create staff activity log:", logErr);
      }

      // Notify parent to refresh user data
      if (onAvatarUpdated) {
        onAvatarUpdated(res.data?.user?.selected_Avatar);
      }

      // Auto-close after short delay
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err) {
      console.error("Error updating avatar:", err);
      setError(
        err?.response?.data?.message ||
          "Failed to update profile picture. Please try again."
      );
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="change-avatar-modal">
      <Box sx={style}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#c51369",
          }}
        >
          <FaTimes />
        </IconButton>

        <h2 className="mb-2 text-center text-2xl font-bold text-navy-700">
          Change Profile Picture
        </h2>
        <p className="mb-5 text-center text-sm text-gray-500">
          For user:{" "}
          <span className="font-medium text-gray-700">
            {userDetails?.fullName || userDetails?.nickName}
          </span>
        </p>

        {/* Tab Switcher */}
        <div className="mb-5 flex rounded-lg bg-gray-100 p-1">
          <button
            onClick={() => {
              setActiveMode("upload");
              setError("");
            }}
            className={`flex-1 rounded-md py-2.5 text-sm font-medium transition-all ${
              activeMode === "upload"
                ? "bg-[#c51369] text-white shadow-sm"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <MdCloudUpload className="mr-1.5 inline-block text-lg" />
            Upload New
          </button>
          <button
            onClick={() => {
              setActiveMode("existing");
              setError("");
            }}
            className={`flex-1 rounded-md py-2.5 text-sm font-medium transition-all ${
              activeMode === "existing"
                ? "bg-[#c51369] text-white shadow-sm"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            Choose Existing
          </button>
        </div>

        {/* Error / Success Messages */}
        {error && (
          <p className="mb-3 rounded-md bg-red-50 p-2 text-center text-sm text-red-600">
            {error}
          </p>
        )}
        {success && (
          <p className="mb-3 rounded-md bg-green-50 p-2 text-center text-sm text-green-600">
            {success}
          </p>
        )}

        {/* Upload Mode */}
        {activeMode === "upload" && (
          <div className="flex flex-col items-center">
            {/* Current Avatar Preview */}
            <div className="relative mb-4">
              <img
                className="h-28 w-28 rounded-full border-4 border-gray-200 object-cover"
                src={
                  previewUrl ||
                  (typeof userDetails?.selected_Avatar === "object"
                    ? userDetails?.selected_Avatar?.avatarUrl
                    : userDetails?.selected_Avatar) ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    userDetails?.fullName || "User"
                  )}&background=c51369&color=fff`
                }
                alt="Preview"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    userDetails?.fullName || "User"
                  )}&background=c51369&color=fff`;
                }}
              />
              {previewUrl && (
                <div className="absolute -right-1 -top-1 flex h-7 w-7 items-center justify-center rounded-full bg-green-500 text-white shadow">
                  <FaCheck className="text-xs" />
                </div>
              )}
            </div>

            {/* Upload Area */}
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="mb-2 flex items-center gap-2 rounded-lg border-2 border-dashed border-gray-300 px-8 py-4 text-gray-500 transition-colors hover:border-[#c51369] hover:text-[#c51369]"
            >
              <FaCamera className="text-lg" />
              <span className="text-sm font-medium">
                {selectedFile ? "Change Image" : "Select Image"}
              </span>
            </button>
            {selectedFile && (
              <p className="mb-2 text-xs text-gray-400">
                {selectedFile.name} (
                {(selectedFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
            <p className="text-xs text-gray-400">
              Supported: JPG, PNG, WebP • Max 5MB
            </p>
          </div>
        )}

        {/* Existing Avatar Selection Mode */}
        {activeMode === "existing" && (
          <div>
            {loadingAvatars ? (
              <div className="flex justify-center py-8">
                <BtnLoader />
              </div>
            ) : avatars.length === 0 ? (
              <p className="py-8 text-center text-gray-500">
                No avatars available
              </p>
            ) : (
              <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-100 p-3">
                <div className="grid grid-cols-4 gap-3 sm:grid-cols-5">
                  {avatars.map((avatar) => (
                    <button
                      key={avatar._id}
                      onClick={() => {
                        setSelectedAvatarId(avatar._id);
                        setPreviewUrl(null);
                        setSelectedFile(null);
                        setError("");
                      }}
                      className={`relative overflow-hidden rounded-xl border-2 transition-all ${
                        selectedAvatarId === avatar._id
                          ? "border-[#c51369] ring-2 ring-[#c51369] ring-offset-1"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <img
                        src={avatar.avatarUrl}
                        alt="Avatar"
                        className="h-full w-full object-cover aspect-square"
                      />
                      {selectedAvatarId === avatar._id && (
                        <div className="absolute inset-0 flex items-center justify-center bg-[#c51369]/20">
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#c51369] text-white">
                            <FaCheck className="text-xs" />
                          </div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-md border border-gray-300 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={
              saving ||
              uploading ||
              (activeMode === "upload" && !selectedFile) ||
              (activeMode === "existing" && !selectedAvatarId)
            }
            className="flex flex-1 items-center justify-center rounded-md bg-[#c51369] py-2.5 text-sm font-medium text-white transition hover:bg-[#a10f5b] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? (
              <BtnLoader />
            ) : uploading ? (
              "Uploading..."
            ) : (
              "Update Profile Picture"
            )}
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default ChangeAvatarModal;
